/**
 * The agency's own books, checked against each other.
 *
 * No outside source knows an agency's commission or its deal schedules, so
 * these are balances rather than second opinions: records that must agree
 * with other records in the same book. Each finding names both sides and the
 * gap. Nothing is changed.
 *
 *   Commission booked on something that no longer earns it. The ledger sync
 *   adds and re-prices rows but has no way to remove one, so a contract
 *   closed early keeps its booked fee (JB, 30 Sep 2026: $10,200 on three
 *   free-agent contracts terminated on 23 Sep).
 *   A ledger row whose own arithmetic does not add up: gross less expenses
 *   is net, and the agent's and agency's shares make the net.
 *   A signed deal whose payment schedule does not add up to its value, or
 *   whose stored commission is not its rate on its value.
 *   A deal instalment marked paid with no income recorded for it — the
 *   athlete's portal would not show money the deal says arrived.
 *   More fees collected from a client than were ever booked for them.
 *
 * Pure; the Money page runs it on what it loads.
 */

export interface BalanceLedgerRow {
  id: string
  transaction_type: string
  gross_amount?: number | null
  expenses_deducted?: number | null
  net_amount?: number | null
  agent_share?: number | null
  agency_share?: number | null
  contract_id?: string | null
  deal_id?: string | null
  client_id?: string | null
}

export interface BalanceContract { id: string; title?: string | null; status?: string | null; client_id?: string | null }

export interface BalanceDeal {
  id: string
  title?: string | null
  stage?: string | null
  value?: number | null
  commission_rate?: number | null
  commission_amount?: number | null
  payment_schedule?: Array<{ date?: string; amount?: number | null; status?: string | null; description?: string | null }> | null
  client_id?: string | null
}

export interface BalanceIncome { deal_id?: string | null; amount: number }
export interface BalanceFee { client_id?: string | null; amount: number }

export type BalanceKind =
  | 'booked_on_closed' | 'booked_without_source' | 'ledger_arithmetic'
  | 'schedule_total' | 'deal_commission' | 'paid_unrecorded' | 'fees_over_booked'

export interface BalanceFinding {
  kind: BalanceKind
  /** The record the finding is about: a ledger row, deal or client. */
  subject_id: string
  client_id: string | null
  /** The money out of balance, where there is one figure for it. */
  gap: number | null
  text: string
}

const money = (n: number) => `$${(Math.round(n * 100) / 100).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
const n = (v: unknown) => (v == null || !Number.isFinite(Number(v)) ? null : Number(v))
const off = (a: number, b: number) => Math.abs(a - b) > 1

export function checkBalances(input: {
  ledger: BalanceLedgerRow[]
  contracts: BalanceContract[]
  deals: BalanceDeal[]
  /** Athlete income recorded against deals. */
  dealIncome: BalanceIncome[]
  /** Fee income collected (the fee transaction types). */
  fees: BalanceFee[]
  /** Client id → name, for the sentences. */
  names: Map<string, string>
}): BalanceFinding[] {
  const out: BalanceFinding[] = []
  const who = (id: string | null | undefined) => (id ? input.names.get(id) ?? 'A client' : 'A client')
  const contracts = new Map(input.contracts.map(c => [c.id, c]))
  const deals = new Map(input.deals.map(d => [d.id, d]))

  // ── The commission ledger ──
  const bookedByClient = new Map<string, number>()
  for (const r of input.ledger) {
    if (r.transaction_type !== 'commission_earned') continue
    const gross = n(r.gross_amount) ?? 0
    // A reversed row books nothing; its notes keep what it held.
    if (gross === 0 && (n(r.net_amount) ?? 0) === 0) continue
    if (r.client_id) bookedByClient.set(r.client_id, (bookedByClient.get(r.client_id) ?? 0) + gross)
    const contract = r.contract_id ? contracts.get(r.contract_id) : null
    const deal = r.deal_id ? deals.get(r.deal_id) : null
    if (r.contract_id && !contract) {
      out.push({ kind: 'booked_without_source', subject_id: r.id, client_id: r.client_id ?? null, gap: gross, text: `${money(gross)} of commission is booked for ${who(r.client_id)} on a contract that no longer exists.` })
    } else if (r.deal_id && !deal) {
      out.push({ kind: 'booked_without_source', subject_id: r.id, client_id: r.client_id ?? null, gap: gross, text: `${money(gross)} of commission is booked for ${who(r.client_id)} on a deal that no longer exists.` })
    } else if (contract && contract.status !== 'active') {
      out.push({ kind: 'booked_on_closed', subject_id: r.id, client_id: r.client_id ?? null, gap: gross, text: `${money(gross)} of commission is still booked for ${who(r.client_id)} on ${contract.title || 'a contract'}, which is ${contract.status || 'not active'}.` })
    } else if (deal && deal.stage !== 'Executed') {
      out.push({ kind: 'booked_on_closed', subject_id: r.id, client_id: r.client_id ?? null, gap: gross, text: `${money(gross)} of commission is still booked for ${who(r.client_id)} on ${deal.title || 'a deal'}, which is at ${deal.stage || 'no stage'}, not signed.` })
    }
    const [exp, net, agent, agency] = [n(r.expenses_deducted) ?? 0, n(r.net_amount), n(r.agent_share), n(r.agency_share)]
    if (net != null && off(gross - exp, net)) {
      out.push({ kind: 'ledger_arithmetic', subject_id: r.id, client_id: r.client_id ?? null, gap: gross - exp - net, text: `A ledger row for ${who(r.client_id)} books ${money(gross)} less ${money(exp)} of expenses as ${money(net)} net; it should be ${money(gross - exp)}.` })
    }
    if (net != null && agent != null && agency != null && off(agent + agency, net)) {
      out.push({ kind: 'ledger_arithmetic', subject_id: r.id, client_id: r.client_id ?? null, gap: net - agent - agency, text: `A ledger row for ${who(r.client_id)} splits ${money(net)} net into ${money(agent)} for the agent and ${money(agency)} for the agency, which is ${money(agent + agency)}.` })
    }
  }

  // ── Signed deals ──
  const incomeByDeal = new Map<string, number[]>()
  for (const i of input.dealIncome) if (i.deal_id) incomeByDeal.set(i.deal_id, [...(incomeByDeal.get(i.deal_id) ?? []), Number(i.amount)])
  for (const d of input.deals) {
    if (d.stage !== 'Executed') continue
    const value = n(d.value)
    const schedule = Array.isArray(d.payment_schedule) ? d.payment_schedule : []
    const scheduled = schedule.reduce((s, p) => s + (n(p.amount) ?? 0), 0)
    const title = d.title || 'A deal'
    if (value != null && schedule.length > 0 && off(scheduled, value)) {
      out.push({ kind: 'schedule_total', subject_id: d.id, client_id: d.client_id ?? null, gap: scheduled - value, text: `${title} (${who(d.client_id)}) is worth ${money(value)}, but its payment schedule adds up to ${money(scheduled)}.` })
    }
    const rate = n(d.commission_rate)
    const stored = n(d.commission_amount)
    if (value != null && rate != null && stored != null && off(stored, (value * rate) / 100)) {
      out.push({ kind: 'deal_commission', subject_id: d.id, client_id: d.client_id ?? null, gap: stored - (value * rate) / 100, text: `${title} (${who(d.client_id)}) stores ${money(stored)} of commission; ${rate}% of ${money(value)} is ${money((value * rate) / 100)}.` })
    }
    // Each paid instalment needs its own income record: one record answers one instalment.
    const unclaimed = [...(incomeByDeal.get(d.id) ?? [])]
    for (const p of schedule) {
      if (p.status !== 'paid') continue
      const amount = n(p.amount)
      if (amount == null) continue
      const i = unclaimed.findIndex(x => !off(x, amount))
      if (i >= 0) { unclaimed.splice(i, 1); continue }
      out.push({ kind: 'paid_unrecorded', subject_id: d.id, client_id: d.client_id ?? null, gap: amount, text: `${title} (${who(d.client_id)}) has a ${money(amount)} instalment${p.date ? ` due ${String(p.date).slice(0, 10)}` : ''} marked paid, with no income recorded for it — the athlete's portal does not show it.` })
    }
  }

  // ── Fees collected against booked ──
  const collected = new Map<string, number>()
  for (const f of input.fees) if (f.client_id) collected.set(f.client_id, (collected.get(f.client_id) ?? 0) + Number(f.amount))
  for (const [clientId, got] of collected) {
    const booked = bookedByClient.get(clientId) ?? 0
    if (got > booked + 1) {
      out.push({ kind: 'fees_over_booked', subject_id: clientId, client_id: clientId, gap: got - booked, text: `${money(got)} in fees has been collected from ${who(clientId)}, but only ${money(booked)} was ever booked — commission is missing from the ledger.` })
    }
  }
  return out
}
